import { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

interface ArbitrageOpportunity {
  coin: string;
  coinId: string;
  buyExchange: string;
  buyExchangeId: string;
  buyPrice: number;
  sellExchange: string;
  sellExchangeId: string;
  sellPrice: number;
  spreadPct: number;
  feesPct: number;
  netGainPct: number;
  estimatedGainUsd: number;
  profitable: boolean;
}

const COIN_COLORS: Record<string, string> = {
  BTC: 'bg-orange-100 text-orange-700',
  ETH: 'bg-blue-100 text-blue-700',
  XRP: 'bg-cyan-100 text-cyan-700',
  SOL: 'bg-purple-100 text-purple-700',
  BNB: 'bg-yellow-100 text-yellow-700',
};

function formatUsd(value: number): string {
  if (value >= 1000) return `$${value.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  if (value >= 1) return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return `$${value.toFixed(4)}`;
}

export const ArbitragePage: React.FC = () => {
  const [opportunities, setOpportunities] = useState<ArbitrageOpportunity[]>([]);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [countdown, setCountdown] = useState(60);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/arbitrage`);
      const data = await res.json();
      setOpportunities(data.opportunities || []);
      setUpdatedAt(data.updatedAt);
      setCountdown(60);
    } catch {
      // silent — keep previous data
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 60_000);
    return () => clearInterval(interval);
  }, [fetchData]);

  useEffect(() => {
    const tick = setInterval(() => setCountdown((c) => (c > 0 ? c - 1 : 60)), 1000);
    return () => clearInterval(tick);
  }, []);

  return (
    <>
      <Helmet>
        <title>Arbitragem de Cripto em Tempo Real | Cripto News</title>
        <meta name="description" content="Veja oportunidades de arbitragem entre corretoras de criptomoedas em tempo real. Diferenças de preço, taxas e ganho líquido estimado para BTC, ETH, XRP, SOL e BNB." />
        <link rel="canonical" href={`${import.meta.env.VITE_SITE_URL || 'https://criptonews.site'}/arbitrage`} />
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4 max-w-5xl">

          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  ⚡ Arbitragem em Tempo Real
                </h1>
                <p className="text-gray-500 mt-1 text-sm">
                  Oportunidades de compra/venda entre corretoras — atualiza a cada 60 segundos
                </p>
              </div>
              <div className="flex items-center gap-3">
                {updatedAt && (
                  <span className="text-xs text-gray-400">
                    Atualizado: {new Date(updatedAt).toLocaleTimeString('pt-BR')}
                  </span>
                )}
                <div className="flex items-center gap-1.5 bg-white border rounded-full px-3 py-1 text-sm text-gray-600 shadow-sm">
                  <span className={`w-2 h-2 rounded-full ${countdown > 10 ? 'bg-green-500' : 'bg-orange-400'} animate-pulse`} />
                  Próx. atualização: {countdown}s
                </div>
                <button
                  onClick={fetchData}
                  className="bg-blue-600 hover:bg-blue-700 text-white text-sm px-4 py-1.5 rounded-full transition"
                >
                  Atualizar
                </button>
              </div>
            </div>
          </div>

          {/* Disclaimer */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5 mb-6 text-xs text-amber-700 flex gap-2 items-start">
            <span className="mt-0.5">⚠️</span>
            <span>
              <strong>Aviso:</strong> Os dados abaixo são apenas informativos e não constituem recomendação de investimento.
              Preços mudam em milissegundos. Taxas de saque, câmbio e liquidez não estão incluídos no cálculo.
              O usuário é exclusivamente responsável por qualquer operação realizada.
            </span>
          </div>

          {/* Content */}
          {loading && opportunities.length === 0 ? (
            <div className="bg-white rounded-xl shadow p-12 text-center">
              <div className="inline-block w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-3" />
              <p className="text-gray-500">Buscando oportunidades nas corretoras...</p>
            </div>
          ) : opportunities.length === 0 ? (
            <div className="bg-white rounded-xl shadow p-12 text-center text-gray-500">
              Nenhuma oportunidade lucrativa encontrada no momento. Tente novamente em breve.
            </div>
          ) : (
            <>
              {/* Summary cards */}
              {(() => {
                const profitable = opportunities.filter((o) => o.profitable);
                const best = opportunities[0];
                return (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-white rounded-xl shadow p-4">
                      <p className="text-xs text-gray-500 mb-1">Total analisadas</p>
                      <p className="text-2xl font-bold text-gray-900">{opportunities.length}</p>
                    </div>
                    <div className="bg-white rounded-xl shadow p-4">
                      <p className="text-xs text-gray-500 mb-1">Lucrativas (pós-taxas)</p>
                      <p className={`text-2xl font-bold ${profitable.length > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                        {profitable.length}
                      </p>
                    </div>
                    <div className="bg-white rounded-xl shadow p-4">
                      <p className="text-xs text-gray-500 mb-1">Melhor spread</p>
                      <p className="text-2xl font-bold text-blue-600">+{best?.spreadPct}%</p>
                    </div>
                    <div className="bg-white rounded-xl shadow p-4">
                      <p className="text-xs text-gray-500 mb-1">Melhor ganho líq.</p>
                      <p className={`text-2xl font-bold ${(best?.netGainPct ?? 0) >= 0 ? 'text-green-600' : 'text-red-400'}`}>
                        {(best?.netGainPct ?? 0) >= 0 ? '+' : ''}{best?.netGainPct}%
                      </p>
                    </div>
                  </div>
                );
              })()}

              {/* Table */}
              <div className="bg-white rounded-xl shadow overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-gray-500 uppercase text-xs">
                      <tr>
                        <th className="px-4 py-3 text-left">Moeda</th>
                        <th className="px-4 py-3 text-left">Comprar em</th>
                        <th className="px-4 py-3 text-right">Preço compra</th>
                        <th className="px-4 py-3 text-left">Vender em</th>
                        <th className="px-4 py-3 text-right">Preço venda</th>
                        <th className="px-4 py-3 text-right">Spread</th>
                        <th className="px-4 py-3 text-right">Taxas</th>
                        <th className="px-4 py-3 text-right">Ganho líq.</th>
                        <th className="px-4 py-3 text-right">Est. p/ $1k</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {opportunities.map((op, i) => (
                        <tr key={i} className="hover:bg-gray-50 transition">
                          <td className="px-4 py-3">
                            <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-bold ${COIN_COLORS[op.coin] || 'bg-gray-100 text-gray-700'}`}>
                              {op.coin}
                            </span>
                          </td>
                          <td className="px-4 py-3 font-medium text-gray-800">{op.buyExchange}</td>
                          <td className="px-4 py-3 text-right text-gray-600">{formatUsd(op.buyPrice)}</td>
                          <td className="px-4 py-3 font-medium text-gray-800">{op.sellExchange}</td>
                          <td className="px-4 py-3 text-right text-gray-600">{formatUsd(op.sellPrice)}</td>
                          <td className="px-4 py-3 text-right text-blue-600 font-semibold">+{op.spreadPct}%</td>
                          <td className="px-4 py-3 text-right text-red-400">-{op.feesPct}%</td>
                          <td className="px-4 py-3 text-right">
                            <span className={`font-bold ${op.profitable ? 'text-green-600' : 'text-red-400'}`}>
                              {op.netGainPct >= 0 ? '+' : ''}{op.netGainPct}%
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className={`font-bold ${op.profitable ? 'text-green-600' : 'text-red-400'}`}>
                              {op.estimatedGainUsd >= 0 ? '+' : ''}${op.estimatedGainUsd}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>


              <p className="text-center text-xs text-gray-400 mt-4">
                Dados via CoinGecko • Taxas estimadas por corretora • Preços em USD
              </p>

              {/* Conteúdo explicativo indexável */}
              <div className="mt-10 space-y-6">
                <section className="bg-white rounded-xl shadow p-6 md:p-8">
                  <h2 className="text-xl font-bold text-gray-900 mb-3">O que é arbitragem de criptomoedas?</h2>
                  <div className="text-gray-700 text-[15px] leading-relaxed space-y-3">
                    <p>
                      Arbitragem é a prática de comprar um ativo em um mercado onde ele está mais barato e
                      vendê-lo em outro onde está mais caro, lucrando com a diferença de preço. No mercado
                      cripto, o mesmo Bitcoin pode ser negociado a preços ligeiramente diferentes em dezenas
                      de exchanges ao mesmo tempo, porque cada uma tem seu próprio livro de ofertas.
                    </p>
                    <p>
                      O monitor acima compara, a cada minuto, os preços de BTC, ETH, XRP, SOL e BNB nas
                      principais exchanges e calcula o spread bruto, as taxas estimadas de negociação e o
                      ganho líquido teórico de cada rota.
                    </p>
                  </div>
                </section>

                <section className="bg-white rounded-xl shadow p-6 md:p-8">
                  <h2 className="text-xl font-bold text-gray-900 mb-3">Oportunidade teórica ≠ lucro realizável</h2>
                  <div className="text-gray-700 text-[15px] leading-relaxed space-y-3">
                    <p>
                      A diferença exibida na tabela é uma <strong>oportunidade teórica</strong>, calculada com
                      preços de referência. Na prática, vários fatores reduzem — ou eliminam — o ganho:
                    </p>
                    <ul className="list-disc pl-5 space-y-2">
                      <li>
                        <strong>Taxas de negociação:</strong> cada exchange cobra taxa na compra e na venda
                        (já estimadas na coluna "Taxas"), além de possíveis taxas de saque.
                      </li>
                      <li>
                        <strong>Slippage:</strong> ao executar a ordem, o preço pode se mover contra você —
                        especialmente em ordens grandes ou em livros com pouca profundidade.
                      </li>
                      <li>
                        <strong>Liquidez:</strong> o preço exibido vale para volumes pequenos; volumes maiores
                        "consomem" o livro de ofertas e pioram o preço médio de execução.
                      </li>
                      <li>
                        <strong>Tempo de transferência:</strong> mover cripto entre exchanges leva de minutos a
                        horas, dependendo da rede — e o spread pode desaparecer nesse intervalo. Manter saldo
                        nas duas exchanges elimina a transferência, mas dobra o capital necessário.
                      </li>
                      <li>
                        <strong>Custos de rede:</strong> taxas de blockchain (gás) para transferir os ativos.
                      </li>
                    </ul>
                    <p>
                      Por isso, spreads pequenos (abaixo de ~1%) raramente são lucrativos para pessoas físicas
                      após todos os custos. Arbitragem profissional é dominada por sistemas automatizados que
                      operam em milissegundos.
                    </p>
                  </div>
                </section>

                <section className="bg-white rounded-xl shadow p-6 md:p-8">
                  <h2 className="text-xl font-bold text-gray-900 mb-3">Como ler a tabela</h2>
                  <ul className="text-gray-700 text-[15px] leading-relaxed list-disc pl-5 space-y-2">
                    <li><strong>Spread:</strong> diferença bruta entre o preço de venda e o de compra.</li>
                    <li><strong>Taxas:</strong> soma das taxas de negociação estimadas das duas exchanges.</li>
                    <li><strong>Ganho líq.:</strong> spread menos taxas — ainda sem considerar slippage, saque e transferência.</li>
                    <li><strong>Est. p/ $1k:</strong> ganho líquido estimado para uma operação de US$ 1.000, nas mesmas condições teóricas.</li>
                  </ul>
                  <p className="text-gray-700 text-[15px] leading-relaxed mt-3">
                    O horário da última atualização aparece no topo da página; os dados vêm da API pública da
                    CoinGecko e são atualizados a cada 60 segundos.
                  </p>
                </section>

                <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-xs text-amber-800 leading-relaxed">
                  <strong>⚠️ Aviso:</strong> este monitor é educativo e informativo. Arbitragem não é lucro
                  garantido — envolve riscos de execução, liquidez, transferência e contraparte. Nada aqui
                  constitui recomendação de investimento.
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};
